import { useEffect, useRef, useState } from 'react';

interface RandomIconsBackgroundProps {
  iconCount?: number;
  minSize?: number;
  maxSize?: number;
  maxTries?: number;
}

export default function RandomIconsBackground({
  iconCount = 30,
  minSize = 10,
  maxSize = 40,
  maxTries = 10
}: RandomIconsBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setDimensions({ width: rect.width, height: rect.height });
    };

    // Initial update
    updateDimensions();

    // Update on resize
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || dimensions.width === 0 || dimensions.height === 0) return;

    // Clear any existing icons
    container.innerHTML = '';

    const placedIcons: { x: number; y: number; size: number }[] = [];

    // Function to check if two icons overlap
    const overlaps = (x1: number, y1: number, size1: number, x2: number, y2: number, size2: number) => {
      return !(
        x2 > x1 + size1 ||
        x2 + size2 < x1 ||
        y2 > y1 + size1 ||
        y2 + size2 < y1
      );
    };

    for (let i = 0; i < iconCount; i++) {
      let placed = false;
      let tries = 0;

      while (!placed && tries < maxTries) {
        tries++;
        const size = minSize + Math.random() * (maxSize - minSize);
        const x = Math.random() * (dimensions.width - size);
        const y = Math.random() * (dimensions.height - size);

        let collision = false;
        for (const iconData of placedIcons) {
          if (overlaps(x, y, size, iconData.x, iconData.y, iconData.size)) {
            collision = true;
            break;
          }
        }

        if (!collision) {
          const icon = document.createElement('div');
          icon.style.cssText = `
            position: absolute;
            left: ${x}px;
            top: ${y}px;
            width: ${size}px;
            height: ${size}px;
            background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20x%3D%224%22%20y%3D%222%22%20width%3D%2216%22%20height%3D%2216%22%20rx%3D%223%22%20stroke%3D%22%23000%22%20stroke-width%3D%222%22%20fill%3D%22none%22%2F%3E%3Cpath%20d%3D%22M8%2011%20l3%203%20l5%20-6%22%20stroke%3D%22%23000%22%20stroke-width%3D%222%22%20fill%3D%22none%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3Cline%20x1%3D%220%22%20y1%3D%2218%22%20x2%3D%2224%22%20y2%3D%2218%22%20stroke%3D%22%23000%22%20stroke-width%3D%222%22%2F%3E%3C%2Fsvg%3E");
            background-repeat: no-repeat;
            background-size: contain;
            opacity: 0.1;
            transform: rotate(${Math.random() * 90 - 45}deg);
            pointer-events: none;
          `;
          container.appendChild(icon);
          placedIcons.push({ x, y, size });
          placed = true;
        }
      }
    }
  }, [dimensions, iconCount, minSize, maxSize, maxTries]);

  return (
    <div 
      ref={containerRef} 
      className="absolute inset-0 overflow-hidden"
      aria-hidden="true"
      style={{ minHeight: '100%' }}
    />
  );
}